import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
} from '@material-ui/core';
import includes from 'lodash/includes';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';

import FormField from '../../../lib/components/form/formField';
import encryptor from '../../../lib/utils/encryptor';
import { composeValidators, isValidUserPoolId, required } from '../../../lib/utils/form';
import { getStoredConnections } from '../../../store/utils/authStorage';

/* eslint-disable no-underscore-dangle */

const EditConnectionDialog = ({ oldConnectionKey, open, onClose, onSave, onDelete }) => {
    const [authType, setAuthType] = useState('');
    const [initialValues, setInitialValues] = useState(null);
    const storedConnections = getStoredConnections();
    const connectionsKeys = Object.keys(storedConnections);

    useEffect(() => {
        const oldConnection = getStoredConnections()[oldConnectionKey];
        const _intialValues = {
            name: oldConnectionKey,
            graphqlEndpoint: encryptor.decrypt(oldConnection.graphqlEndpoint),
            apiKey: oldConnection.apiKey && encryptor.decrypt(oldConnection.apiKey),
            authorizationToken: oldConnection.authorizationToken && encryptor.decrypt(oldConnection.authorizationToken),
            userpoolId: oldConnection.userpoolId && encryptor.decrypt(oldConnection.userpoolId),
            userpoolClientId: oldConnection.userpoolClientId && encryptor.decrypt(oldConnection.userpoolClientId),
            oktaServer: oldConnection.oktaServer && encryptor.decrypt(oldConnection.oktaServer),
            oidcIssuer: oldConnection.oidcIssuer && encryptor.decrypt(oldConnection.oidcIssuer),
            oidcClientId: oldConnection.oidcClientId && encryptor.decrypt(oldConnection.oidcClientId),
        };
        setAuthType(oldConnection.authType);
        setInitialValues(_intialValues);
    }, [oldConnectionKey]);

    const isValidName = value => value !== oldConnectionKey && (includes(connectionsKeys, value) ? 'invalid' : undefined);
    const updateAuthType = event => setAuthType(event.currentTarget.value);
    const onSubmit = data => onSave({ authType, ...data });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby='edit_connection_form'
            maxWidth='sm'
            disableBackdropClick
        >
            <DialogTitle id='edit_connection_form'>Edit connection</DialogTitle>

            <DialogContent id='edit_connection_form--content'>
                <FormControl component='fieldset'>
                    <FormLabel component='legend'>Authentication Method</FormLabel>
                    <RadioGroup
                        aria-label='Authentication Method'
                        name='authenticationMethod'
                        value={authType}
                        onChange={updateAuthType}
                    >
                        <FormControlLabel value='apiKey' control={<Radio color='primary' />} label='API Key' />
                        <FormControlLabel value='lambda' control={<Radio color='primary' />} label='Lambda' />
                        <FormControlLabel value='cognito' control={<Radio color='primary' />} label='Cognito' />
                        <FormControlLabel value='okta' control={<Radio color="primary" />} label="Okta" />
                        <FormControlLabel value='oidc' control={<Radio color="primary" />} label="OIDC" />
                    </RadioGroup>
                </FormControl>
                <Form
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                    render={({ handleSubmit, invalid, pristine }) => {
                        return (
                            <form onSubmit={handleSubmit} noValidate>

                                <FormField
                                    name='name'
                                    id='name'
                                    label='Connection Name'
                                    validate={composeValidators(isValidName, required)}
                                    autoFocus
                                    required
                                />

                                <FormField
                                    name='graphqlEndpoint'
                                    id='graphqlEndpoint'
                                    label='GraphQL Endpoint'
                                    validate={required}
                                    maxLength={200}
                                    required
                                    submitOnEnter
                                />

                                {authType === 'apiKey' && (
                                    <FormField
                                        name='apiKey'
                                        id='apiKey'
                                        label='API Key'
                                        required={false}
                                        submitOnEnter
                                    />
                                )}

                                {authType === 'lambda' && (
                                    <FormField
                                        name='authorizationToken'
                                        id='authorizationToken'
                                        label='Authorization Token'
                                        required
                                        submitOnEnter
                                    />
                                )}

                                {authType === 'cognito' && (
                                    <>
                                        <FormField
                                            name='userpoolId'
                                            id='userpoolId'
                                            label='Cognito Userpool Id'
                                            validate={composeValidators(required, isValidUserPoolId)}
                                            required
                                        />

                                        <FormField
                                            name='userpoolClientId'
                                            id='userpoolClientId'
                                            label='Cognito Userpool Client Id'
                                            validate={required}
                                            required
                                        />

                                        <FormField
                                            name='username'
                                            id='username'
                                            label='Username'
                                            validate={required}
                                            required
                                        />

                                        <FormField
                                            name='password'
                                            id='password'
                                            label='Password'
                                            type='password'
                                            validate={required}
                                            required
                                            submitOnEnter
                                        />
                                    </>
                                )}

                                {authType === 'okta' && (
                                    <>
                                        <FormField
                                            name="oktaServer"
                                            id="oktaServer"
                                            label="Okta Server"
                                            validate={required}
                                            maxLength={200}
                                            required
                                        />

                                        <FormField
                                            name="userpoolClientId"
                                            id="userpoolClientId"
                                            label="Cognito Client Id"
                                            validate={required}
                                            required
                                        />

                                        <div>
                                            Make sure the cognito client has <code style={{ color: "blue" }}>{document.location.protocol}{"//"}{document.location.host}/sso/okta</code> in the Callback URL(s)
                                        </div>
                                    </>
                                )}

                                {authType === 'oidc' && (
                                    <>
                                        <FormField
                                            name="oidcIssuer"
                                            id="oidcIssuer"
                                            label="OIDC Server"
                                            validate={required}
                                            maxLength={200}
                                            required
                                        />

                                        <FormField
                                            name="oidcClientId"
                                            id="oidcClientId"
                                            label="OIDC Client Id"
                                            validate={required}
                                            required
                                        />

                                        <div>
                                            Make sure the oidc client has <code style={{ color: "blue" }}>{document.location.protocol}{"//"}{document.location.host}/sso/oidc</code> in the Callback URL(s)
                                        </div>
                                    </>
                                )}

                                <DialogActions>
                                    <div className="secondary-actions">
                                        <Button onClick={onDelete} color='primary'>Delete</Button>
                                    </div>
                                    <div className="primary-actions">
                                        <Button onClick={onClose} color='secondary'>
                                            Cancel
                                        </Button>

                                        <Button type='submit' color='primary' disabled={invalid || pristine}>
                                            Save
                                        </Button>
                                    </div>
                                </DialogActions>
                            </form>
                        );
                    }}
                />
            </DialogContent>
        </Dialog>
    );
};

EditConnectionDialog.propTypes = {
    oldConnectionKey: PropTypes.string.isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

export default EditConnectionDialog;
